import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type { Edge } from "@cimara/sim";
import type { Spawn } from "@cimara/protocol";
import { buildRoadGeometry, type World } from "@cimara/world";
import {
  buildHillPlacements,
  buildRouteRibbon,
  buildTreePlacements,
  type MeshData,
} from "../lib/geometry";
import { samplePath } from "../lib/route";
import {
  GROUND_FAR,
  GROUND_INNER,
  GROUND_OUTER,
  HILL_GREENS,
  ROAD_COLOR,
  ROAD_MARKING,
  TREE_GREENS,
  TREE_TRUNK,
} from "./palette";

function dataToGeometry(data: MeshData): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(data.normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  geometry.computeBoundingSphere();
  return geometry;
}

function useDisposable<T extends { dispose(): void }>(value: T): T {
  useEffect(() => () => value.dispose(), [value]);
  return value;
}

function Road({ world, routeId }: { world: World; routeId: number | undefined }) {
  const roadGeometry = useMemo(() => {
    const data = buildRoadGeometry(world, routeId);
    return dataToGeometry(data);
  }, [world, routeId]);
  useDisposable(roadGeometry);

  const centreLine = useMemo(
    () => dataToGeometry(buildRouteRibbon(world, routeId, { width: 0.11, yOffset: 0.025 })),
    [world, routeId],
  );
  useDisposable(centreLine);

  const leftEdge = useMemo(
    () =>
      dataToGeometry(
        buildRouteRibbon(world, routeId, {
          width: 0.09,
          offset: (edge: Edge) => edge.width / 2 - 0.45,
          yOffset: 0.025,
        }),
      ),
    [world, routeId],
  );
  useDisposable(leftEdge);

  const rightEdge = useMemo(
    () =>
      dataToGeometry(
        buildRouteRibbon(world, routeId, {
          width: 0.09,
          offset: (edge: Edge) => -(edge.width / 2 - 0.45),
          yOffset: 0.025,
        }),
      ),
    [world, routeId],
  );
  useDisposable(rightEdge);

  const markingMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: ROAD_MARKING,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
      }),
    [],
  );
  useDisposable(markingMaterial);

  return (
    <group>
      <mesh geometry={roadGeometry} receiveShadow={false}>
        <meshLambertMaterial color={ROAD_COLOR} />
      </mesh>
      <mesh geometry={centreLine} material={markingMaterial} />
      <mesh geometry={leftEdge} material={markingMaterial} />
      <mesh geometry={rightEdge} material={markingMaterial} />
    </group>
  );
}

function Land({ world, routeId }: { world: World; routeId: number | undefined }) {
  const inner = useMemo(
    () => dataToGeometry(buildRouteRibbon(world, routeId, { width: 32, yOffset: -0.18 })),
    [world, routeId],
  );
  const outer = useMemo(
    () => dataToGeometry(buildRouteRibbon(world, routeId, { width: 330, yOffset: -2.2 })),
    [world, routeId],
  );
  useDisposable(inner);
  useDisposable(outer);

  const { centre, floorY } = useMemo(() => {
    const { min, max } = world.bounds;
    return {
      centre: [(min.x + max.x) / 2, (min.z + max.z) / 2] as const,
      floorY: min.y - 38,
    };
  }, [world]);

  return (
    <group>
      <mesh geometry={outer} renderOrder={0}>
        <meshLambertMaterial color={GROUND_OUTER} />
      </mesh>
      <mesh geometry={inner} renderOrder={1}>
        <meshLambertMaterial color={GROUND_INNER} />
      </mesh>
      <mesh position={[centre[0], floorY, centre[1]]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[22000, 22000]} />
        <meshBasicMaterial color={GROUND_FAR} />
      </mesh>
    </group>
  );
}

function Trees({ world, routeId }: { world: World; routeId: number | undefined }) {
  const trees = useMemo(() => buildTreePlacements(world, routeId), [world, routeId]);

  const trunkGeometry = useMemo(() => {
    const geometry = new THREE.CylinderGeometry(0.11, 0.17, 1.3, 5, 1);
    geometry.translate(0, 0.65, 0);
    return geometry;
  }, []);
  const foliageGeometry = useMemo(() => {
    const geometry = new THREE.ConeGeometry(1.0, 2.8, 6, 1);
    geometry.translate(0, 2.7, 0);
    return geometry;
  }, []);
  const trunkMaterial = useMemo(
    () => new THREE.MeshLambertMaterial({ color: TREE_TRUNK }),
    [],
  );
  const foliageMaterial = useMemo(
    () =>
      new THREE.MeshLambertMaterial({
        color: "#ffffff",
        flatShading: true,
      }),
    [],
  );
  useDisposable(trunkGeometry);
  useDisposable(foliageGeometry);
  useDisposable(trunkMaterial);
  useDisposable(foliageMaterial);

  const trunkRef = useRef<THREE.InstancedMesh>(null);
  const foliageRef = useRef<THREE.InstancedMesh>(null);

  useLayoutEffect(() => {
    const trunks = trunkRef.current;
    const foliage = foliageRef.current;
    if (!trunks || !foliage) return;

    const dummy = new THREE.Object3D();
    const palette = TREE_GREENS.map((color) => new THREE.Color(color));
    trees.forEach((tree, index) => {
      dummy.position.set(tree.x, tree.y, tree.z);
      dummy.rotation.set(0, tree.rotation, 0);
      dummy.scale.setScalar(tree.scale);
      dummy.updateMatrix();
      trunks.setMatrixAt(index, dummy.matrix);
      foliage.setMatrixAt(index, dummy.matrix);
      foliage.setColorAt(
        index,
        palette[Math.min(palette.length - 1, Math.floor(tree.tint * palette.length))],
      );
    });
    trunks.instanceMatrix.needsUpdate = true;
    foliage.instanceMatrix.needsUpdate = true;
    if (foliage.instanceColor) foliage.instanceColor.needsUpdate = true;
  }, [trees]);

  if (trees.length === 0) return null;

  return (
    <group>
      <instancedMesh
        ref={trunkRef}
        args={[trunkGeometry, trunkMaterial, trees.length]}
        frustumCulled={false}
      />
      <instancedMesh
        ref={foliageRef}
        args={[foliageGeometry, foliageMaterial, trees.length]}
        frustumCulled={false}
      />
    </group>
  );
}

function Hills({ world, routeId }: { world: World; routeId: number | undefined }) {
  const hills = useMemo(() => buildHillPlacements(world, routeId), [world, routeId]);

  const geometry = useMemo(() => {
    const cone = new THREE.ConeGeometry(1, 1, 7, 1);
    cone.translate(0, 0.5, 0);
    return cone;
  }, []);
  const material = useMemo(
    () =>
      new THREE.MeshLambertMaterial({
        color: "#ffffff",
        flatShading: true,
      }),
    [],
  );
  useDisposable(geometry);
  useDisposable(material);

  const meshRef = useRef<THREE.InstancedMesh>(null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const dummy = new THREE.Object3D();
    const palette = HILL_GREENS.map((color) => new THREE.Color(color));
    hills.forEach((hill, index) => {
      dummy.position.set(hill.x, hill.y, hill.z);
      dummy.rotation.set(0, hill.rotation, 0);
      dummy.scale.set(hill.radius, hill.height, hill.radius);
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);
      mesh.setColorAt(
        index,
        palette[Math.min(palette.length - 1, Math.floor(hill.tint * palette.length))],
      );
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [hills]);

  if (hills.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, hills.length]}
      frustumCulled={false}
    />
  );
}

function StartGate({ world, spawn }: { world: World; spawn: Spawn }) {
  const gate = useMemo(() => {
    const edge = world.getEdge(spawn.edge);
    const sample = samplePath(world, spawn.edge, spawn.s);
    if (!sample) return null;
    return { sample, width: edge?.width ?? 6 };
  }, [world, spawn]);

  if (!gate) return null;
  const { sample, width } = gate;

  return (
    <group position={[sample.x, sample.y, sample.z]} rotation={[0, sample.heading, 0]}>
      <mesh position={[0, 0.03, 0]}>
        <boxGeometry args={[width, 0.02, 0.8]} />
        <meshBasicMaterial color={ROAD_MARKING} />
      </mesh>
    </group>
  );
}

export function Course({
  world,
  routeId,
  startSpawn,
}: {
  world: World;
  routeId: number | undefined;
  startSpawn?: Spawn;
}) {
  return (
    <group>
      <Land world={world} routeId={routeId} />
      <Hills world={world} routeId={routeId} />
      <Road world={world} routeId={routeId} />
      <Trees world={world} routeId={routeId} />
      <StartGate world={world} spawn={startSpawn ?? world.spawn} />
    </group>
  );
}
